import { useEffect, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { fetchHeadlinePlaces, fetchPlaceTrip } from "../lib/api";
import { dictionaries } from "../lib/i18n";
import type { Origin, PlaceTrip, Stop, UiLang } from "../lib/types";
import { FoldPanel } from "./FoldPanel";

interface Props {
  lang: UiLang;
  /** Stedene man kan si at man kommer fra — de samme som skjemaet bruker. */
  origins: Origin[];
  /** Åpner hele ruta stedet ligger på, for den som vil ha mer likevel. */
  onOpenRoute: (corridorId: string) => void;
  /**
   * Gjør forhåndsvisningen til en ekte plan om nettopp dette stedet.
   *
   * Panelet viser dagene, men ikke etappene, været, budsjettet, klesrådet
   * eller roadbooken — de hører til en plan, og panelet lager ikke en plan.
   * Uten denne veien måtte man over i rutevelgeren og bygge hele korridoren på
   * nytt for å få dem. Se `focusStopId`.
   */
  onPlanTrip: (stopId: string, days: number, corridorId: string, fromStopId: string) => void;
}

const DAY_CHOICES = [1, 2, 3, 4, 5, 7];

/**
 * «Jeg så en reel om Lofoten og vil bare dit.»
 *
 * ── Hvorfor dette panelet finnes ───────────────────────────────────────────
 *
 * Fordi appen var korridorformet hele veien ned. Hver plan gikk fra A til B
 * langs en strekning, og den som bare ville se Trolltunga måtte planlegge
 * Stavanger–Bergen for å få vite noe som helst om den — hvor lang tid turen
 * tar, hvordan man kommer seg dit, om det går an på en dag.
 *
 * Det er sannsynligvis den vanligste måten folk møter Norge på nå: man ser ett
 * bilde, og reiser dit. Spurt fra bruk med de ordene: «ikke alle er glad i å ta
 * hele veien».
 *
 * ── Hvorfor det står over rutevalget ───────────────────────────────────────
 *
 * Samme grunn som «når bør du komme?»: den som ikke vet at Norge har ti ruter
 * skal møte spørsmålet de faktisk har, før lista over steder de ikke kjenner.
 * Og den som vil ha hele turen likevel, finner ruta stedet ligger på nederst i
 * panelet — ett klikk unna, framfor å være det eneste valget.
 */
export function PlaceTripPanel({ lang, origins, onOpenRoute, onPlanTrip }: Props) {
  const t = dictionaries[lang];
  const [places, setPlaces] = useState<Stop[]>([]);
  const [stopId, setStopId] = useState("");
  const [days, setDays] = useState(3);
  const [from, setFrom] = useState("");
  const [trip, setTrip] = useState<PlaceTrip | null>(null);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  /*
   * Stedene å velge mellom, med ett forsøk til om det første ryker.
   *
   * Første utgave svelget feilen med `.catch(() => setPlaces([]))`, og da ble
   * et nettverksbrudd umulig å skille fra «det finnes ingen steder»: panelet
   * viste en tom nedtrekksmeny som så ut som at appen var i stykker. Et
   * andre forsøk koster lite — svaret er statisk og caches en time — og
   * mobilnett mister den første forespørselen ofte nok til at det er verdt det.
   */
  useEffect(() => {
    let live = true;
    const load = (attempt: number): Promise<void> =>
      fetchHeadlinePlaces(lang)
        .then((result) => {
          if (live) setPlaces(result.places ?? []);
        })
        .catch(() => (attempt < 1 ? load(attempt + 1) : undefined));

    void load(0).finally(() => {
      if (live) setReady(true);
    });
    return () => {
      live = false;
    };
  }, [lang]);

  useEffect(() => {
    if (!stopId) {
      setTrip(null);
      return;
    }
    let live = true;
    setLoading(true);
    fetchPlaceTrip(stopId, days, "avslappet", lang, from || undefined)
      .then((result) => live && setTrip(result))
      .catch(() => live && setTrip(null))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [stopId, days, from, lang]);

  /*
   * Uten steder å velge mellom er det ingenting panelet kan gjøre.
   *
   * Da er det bedre å la det stå over enn å vise en tom nedtrekksmeny — en
   * inngang som ikke fører noe sted ser ut som en feil, og er det også. Dette
   * er `ready`-flagget og ikke `places.length === 0` alene: uten det ville
   * panelet blitt tegnet og så forsvunnet igjen før svaret rakk fram.
   */
  if (ready && places.length === 0) return null;

  /** Setningen i hodet når panelet er lagt sammen. */
  const summary = trip ? trip.stop.name : t.placeTripLead;

  return (
    <FoldPanel
      id="placeTrip"
      title={t.placeTripTitle}
      icon={<MapPin size={14} />}
      summary={summary}
    >
      <p className="card-sub">{t.placeTripHint}</p>

      <div className="place-pick">
        <label className="field">
          <span className="field-label">{t.placeTripPick}</span>
          {/*
            `date-input` er appens egen feltstil, og den er 44 px høy med
            16 px skrift. Uten den var disse to de eneste kontrollene i appen
            uten CSS: rundt 21 px høye systemnedtrekk ved siden av 44 px-feltene
            rett over — og iOS Safari zoomer hele siden når et felt under 16 px
            får fokus.
          */}
          <select
            className="date-input"
            value={stopId}
            onChange={(event) => setStopId(event.target.value)}
          >
            <option value="">{t.placeTripLead}</option>
            {places.map((place) => (
              <option key={place.id} value={place.id}>
                {place.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field-label">{t.placeTripFrom}</span>
          <select
            className="date-input"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          >
            <option value="">{t.placeTripAnywhere}</option>
            {origins.map((origin) => (
              <option key={origin.id} value={origin.id}>
                {origin.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {stopId && (
        <div className="chips place-days" role="group" aria-label={t.placeTripDaysLabel}>
          {DAY_CHOICES.map((choice) => (
            <button
              key={choice}
              type="button"
              className={`chip ${days === choice ? "chip--on" : ""}`}
              aria-pressed={days === choice}
              onClick={() => setDays(choice)}
            >
              {choice}
            </button>
          ))}
        </div>
      )}

      {loading && !trip && (
        <p className="note">
          <Loader2 size={13} className="spin" /> {t.finding}
        </p>
      )}

      {trip && (
        <div className="place-trip">
          {/*
            Det ene tallet som kan få noen til å la være.

            En dagstur til Trolltunga finnes ikke, og det er bedre å få vite det
            før man har bestilt enn på parkeringsplassen.
          */}
          {trip.minDays > 1 && <p className="place-need">{t.placeTripNeed(trip.minDays)}</p>}

          {trip.approach && (
            <p className="note">
              {t.placeTripGetting(
                trip.approach.fromName,
                trip.approach.hours,
                trip.approach.straightKm,
              )}
            </p>
          )}

          <ol className="place-days-list">
            {trip.days.map((day) => (
              <li key={day.dayNumber}>
                <span className="place-day-label">{t.placeTripDay(day.dayNumber)}</span>
                <span className="place-day-stops">
                  {day.stops.map((stop) => stop.name).join(", ")}
                </span>
              </li>
            ))}
          </ol>

          <h4 className="place-near-title">{t.placeTripNearbyTitle}</h4>
          {trip.nearby.length === 0 ? (
            <p className="note">{t.placeTripNothingNear}</p>
          ) : (
            <ul className="place-near">
              {trip.nearby.map((near) => (
                <li key={near.stop.id}>
                  <b>{near.stop.name}</b> <small>{t.placeTripNearby(near.hours)}</small>
                </li>
              ))}
            </ul>
          )}

          {/*
            Veien videre, i to retninger.

            Den første er den forhåndsvisningen alltid manglet: en *ekte* plan
            om dette stedet, med etapper, vær og roadbook. Den står først og
            som knapp, fordi det er det den som valgte ett sted som regel er
            ute etter — dagene over er svaret på «rekker jeg det?», ikke turen.

            Den andre er hele ruta stedet ligger på, for den som fikk lyst på
            mer. Ett klikk unna framfor å være det eneste valget — som det var
            før.
          */}
          <button
            type="button"
            className="place-plan"
            onClick={() =>
              onPlanTrip(trip.stop.id, Math.max(days, trip.minDays), trip.corridorId, from)
            }
          >
            {t.placeTripPlan}
          </button>

          <button
            type="button"
            className="linkish place-open-route"
            onClick={() => onOpenRoute(trip.corridorId)}
          >
            {t.placeTripOpenRoute}
          </button>
        </div>
      )}
    </FoldPanel>
  );
}
